import { useEffect, useState, useRef, useCallback } from 'react'
import { InfiniteScroll, DotLoading, PullToRefresh } from 'antd-mobile'
import styles from './index.scss'
import { useDebounce } from '@/libs/functions/tools'
import Empty from './empty'
import { DpBgTheme } from '@/libs/components'
import { PullStatus } from 'antd-mobile/es/components/pull-to-refresh'
import pick from 'lodash/pick'

// 请求控制器管理器
class RequestControllerManager {
  private controller: AbortController | null = null
  private requestId: number = 0

  // 创建新的请求控制器
  create(): { controller: AbortController; requestId: number } {
    // 取消之前的请求
    this.cancel('New request initiated')
    
    this.controller = new AbortController()
    this.requestId += 1
    
    return {
      controller: this.controller,
      requestId: this.requestId
    }
  }

  // 取消当前请求
  cancel(reason?: string): void {
    if (this.controller) {
      this.controller.abort(reason || 'Request cancelled')
      this.controller = null
    }
  }

  // 获取当前请求ID
  getCurrentRequestId(): number {
    return this.requestId
  }

  // 检查请求是否被取消
  isCancelled(error: any): boolean {
    return error?.name === 'AbortError' || error?.message?.includes('cancelled')
  }
}

const LoadingContainer = () => {
  return (
    <div className={styles['dp-infiniteScrollList--body--placeholder']}>
      <div className={styles['dp-infiniteScrollList--body--placeholder-wrapper']}>
        <DotLoading color='var(--text-color, primary)' />
      </div>
      {lang['正在拼命加载数据']}
    </div>
  )
}

export default (props: any) => {
  const useRefCount = useRef(false) // 是否初次请求
  const useDestruction = useRef(false) // 销毁禁止请求
  const useCurrentTopData = useRef([]) // 置顶数据
  const [dataSource, updateDataSource] = useState<any[]>([]) // 原始数据
  const [hasMore, setHasMore] = useState(props?.manual ? false : props?.hasPage) // 是否more
  const useRefPage = useRef({ current: 1, size: 20 }) // page数据
  const useRefTotal = useRef(20) // 总数
  const useManual = useRef(props?.manual || false) // 是否主动请求
  const [loading, updateLoading] = useState(false)
  
  // 使用ref存储请求管理器，确保在组件生命周期内保持一致
  const requestManagerRef = useRef(new RequestControllerManager())
  
  // 使用ref存储当前请求的requestId，用于数据一致性校验
  const currentRequestIdRef = useRef<number>(0)
  
  // 使用ref标记是否需要重置数据（用于防止旧请求数据覆盖新数据）
  const shouldResetDataRef = useRef(false)

  // 清理函数：取消所有进行中的请求
  const cancelPendingRequests = useCallback(() => {
    requestManagerRef.current.cancel('Component cleanup or new search')
  }, [])

  // 防抖函数，确保快速变化时不会立即请求接口
  const onCgDebounce = useDebounce(
    (isRefresh = false) => {
      // 只有在防抖触发时才发起请求
      fetchMoreData(isRefresh)
    },
    props?.hasPage ? 300 : 500 // 增加防抖时间，确保参数稳定
  )

  function doSearch(isRefresh = false) {
    // 取消之前的请求
    cancelPendingRequests()
    
    // 标记需要重置数据
    shouldResetDataRef.current = true
    
    // 重置分页和状态
    useRefPage.current = {
      current: 1,
      size: 20
    }
    useRefTotal.current = 20
    
    // 只有在刷新时才立即清空数据，避免闪烁
    if (isRefresh) {
      updateDataSource([])
    }
    
    // 触发防抖，只有当参数稳定后才会执行请求
    onCgDebounce(isRefresh)
  }

  async function fetchMoreData(isRefresh = false) {
    if (useDestruction.current) return
    
    // 创建新的请求控制器和请求ID
    const { controller, requestId } = requestManagerRef.current.create()
    currentRequestIdRef.current = requestId
    
    const _currentParams = {
      ...props?.initParams,
      ...props?.params,
      ...(props?.hasPage ? useRefPage.current : {}),
      signal: controller.signal // 传递signal用于取消请求
    }
    
    try {
      if (props?.requiredParams) { // 校验必填数据，如果缺少必填数据，直接禁止请求，防止各种操作产生
        const _query = pick(_currentParams, props.requiredParams)
        if (Object.keys(_query)?.filter(_ => _query[_])?.length < props.requiredParams?.length) return // 说明必填的项传少了
      }
      
      updateLoading(true)
      props?.perfStart?.(_currentParams)
      
      const res: any = await props?.fetchResourceApi?.(_currentParams)
      
      // 检查请求是否已被取消或组件是否已销毁
      if (controller.signal.aborted || useDestruction.current) {
        return
      }
      
      // 检查请求ID是否匹配（防止旧请求数据覆盖新数据）
      if (requestId !== currentRequestIdRef.current) {
        return
      }
      
      updateLoading(false)
      
      if (res?.code === -1) {
        useRefTotal.current = dataSource?.length || 0
        useRefCount.current = true
        updateDataSource((val) => [...(val || [])])
        setHasMore(false)
        props?.errCallback?.(res, _currentParams)
        return
      }
      
      props?.callback?.(res, _currentParams)
      const currentRes = props?.transformData?.(res) || res
      const currentTopDataRes = props?.transformTopData?.(res) || []; // 置顶数据
      
      useRefCount.current = true
      useRefPage.current = {
        current: useRefPage.current?.current + 1,
        size: useRefPage.current?.size || 20
      }
      useCurrentTopData.current = currentTopDataRes
      useRefTotal.current = currentRes?.total || 0
      props?.onChange?.(currentRes, _currentParams)
      
      // 如果需要重置数据（新搜索），则清空旧数据
      if (shouldResetDataRef.current) {
        shouldResetDataRef.current = false
        updateDataSource([...(currentRes?.list || [])])
      } else {
        updateDataSource((val) => [...(val || []), ...(currentRes?.list || [])])
      }
      
      setHasMore(props?.hasPage ? ((currentRes?.current || 1 ) * (currentRes?.size || 20) < (currentRes?.total || 0)) : false)
    } catch (e: any) {
      // 检查是否是请求取消错误
      if (requestManagerRef.current.isCancelled(e)) {
        return
      }
      
      // 检查请求ID是否匹配
      if (requestId !== currentRequestIdRef.current) {
        return
      }
      
      props?.errCallback?.(e, _currentParams)
      updateLoading(false)
      useRefTotal.current = dataSource?.length || 0
      useRefCount.current = true
      setHasMore(false)
    }
  }

  useEffect(() => {
    if (useManual.current) {
      if (props?.setUseManual) {
        props?.setUseManual?.(useManual, props?.params)
      } else {
        useManual.current = false
      }     
      return
    }
    
    // 参数变化时，取消之前的请求并触发防抖搜索
    cancelPendingRequests()
    
    useRefCount.current = false
    useRefPage.current = {
      current: 1,
      size: 20
    }
    useRefTotal.current = 20
    
    // 调用doSearch，它会触发防抖，确保参数稳定后才请求
    doSearch()
    
    // 清理函数
    return () => {
      cancelPendingRequests()
    }
  }, [props?.params])
  
  useEffect(() => {
    return () => {
      useRefCount.current = false
      useRefPage.current = {
        current: 1,
        size: 20
      }
      useRefTotal.current = 20
      useDestruction.current = true
      
      // 组件卸载时取消所有请求
      cancelPendingRequests()
      
      updateDataSource([])
    }
  }, [])

  const statusRecord: Record<PullStatus, string> = {
    pulling: lang['下拉刷新'],
    canRelease: lang['释放立即刷新'],
    refreshing: lang['玩命加载中...'],
    complete: lang['刷新成功']
  }

  const _hasMore = !props?.hasPage ? false : ((dataSource?.length < useRefTotal.current) ? hasMore : false)
  
  return (
    <DpBgTheme {...props?.bgProps}>
      <div className={styles['dp-infiniteScrollList']} id={props?.id} style={props?.style}>
        <div className={styles['dp-infiniteScrollList--header']} style={props?.headerStyle}>
          {props?.searchRender?.(dataSource)}
          {props?.headerRender?.(dataSource)}
        </div>
        <PullToRefresh
          onRefresh={async () => {
            // 下拉刷新时取消之前的请求
            cancelPendingRequests()
            
            useRefCount.current = false
            useRefPage.current = {
              current: 1,
              size: 20
            }
            doSearch(true)
          }}
          renderText={status => {
            return <div>{props?.statusText?.(dataSource)?.[status] || statusRecord[status]}</div>
          }}
        >
          <div className={styles['dp-infiniteScrollList--body']} id={props?.bodyId} style={props?.bodyStyle}>
            {(dataSource?.length || !hasMore || useCurrentTopData?.current?.length) && useRefCount.current ? (
              (dataSource?.length > 0 || useCurrentTopData?.current?.length > 0) ? (
                <>
                  {props?.listRender?.(useCurrentTopData?.current) || useCurrentTopData?.current?.map((item: any) => props?.itemRender?.(item))}
                  {props?.listRender?.(dataSource) || dataSource?.map((item: any, index: number) => props?.itemRender?.(item))}
                  <InfiniteScroll loadMore={() => fetchMoreData(false)} hasMore={_hasMore} threshold={1000}>
                    {
                      _hasMore ? LoadingContainer() : (props?.footerText || <>{lang['没有更多数据啦']}</>)
                    }
                  </InfiniteScroll>
                </>
              ) : (
                <Empty emptyTips={props?.emptyTips} />
              )
            ) : loading ? LoadingContainer() : <></>}
          </div>
        </PullToRefresh>
      </div>
    </DpBgTheme>
  )
}
